<template>
  <div class="workspace-layout" :class="{ 'workspace-layout--mobile': !isPC }">
    <div class="workspace-layout__sidebar">
      <WorkspaceSidebar />
    </div>

    <div class="workspace-layout__topbar">
      <WorkspaceTopbar :mobile="!isPC" @open-sidebar="openMobileSidebar" />
    </div>

    <main class="workspace-layout__content">
      <router-view v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </main>

    <transition name="mobile-sidebar-fade">
      <div
        v-if="isMobileSidebarOpen"
        class="workspace-layout__mobile-sidebar"
        @click.self="closeMobileSidebar"
      >
        <div class="workspace-layout__mobile-sidebar-panel">
          <WorkspaceSidebar mobile @close="closeMobileSidebar" @navigate="closeMobileSidebar" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue';
  import { isPCViewport } from '@/hooks/useViewportAccessGate';
  import WorkspaceSidebar from './WorkspaceSidebar.vue';
  import WorkspaceTopbar from './WorkspaceTopbar.vue';

  const isPC = ref(isPCViewport());
  const isMobileSidebarOpen = ref(false);

  function refreshViewportMode() {
    isPC.value = isPCViewport();
    if (isPC.value) closeMobileSidebar();
  }

  function openMobileSidebar() {
    isMobileSidebarOpen.value = true;
  }

  function closeMobileSidebar() {
    isMobileSidebarOpen.value = false;
  }

  onMounted(() => {
    refreshViewportMode();
    window.addEventListener('resize', refreshViewportMode);
    window.addEventListener('orientationchange', refreshViewportMode);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', refreshViewportMode);
    window.removeEventListener('orientationchange', refreshViewportMode);
  });
</script>

<style scoped>
  .workspace-layout {
    --workspace-sidebar-offset: 8px;
    --workspace-sidebar-width: 240px;
    --workspace-content-gap: 32px;
    --workspace-content-right: 29px;
    --workspace-topbar-height: 68px;
    --workspace-content-top-gap: 16px;
    --workspace-content-bottom: 36px;

    position: relative;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns:
      calc(var(--workspace-sidebar-offset) + var(--workspace-sidebar-width))
      var(--workspace-content-gap) minmax(0, 1fr) var(--workspace-content-right);
    grid-template-rows:
      var(--workspace-topbar-height) var(--workspace-content-top-gap) minmax(0, 1fr)
      var(--workspace-content-bottom);
    overflow: hidden;
    background: #fbf9ff;
  }

  .workspace-layout__sidebar {
    grid-column: 1;
    grid-row: 1 / -1;
    width: var(--workspace-sidebar-width);
    min-height: 0;
    margin: var(--workspace-sidebar-offset) 0 0 var(--workspace-sidebar-offset);
    z-index: 2;
  }

  .workspace-layout__topbar {
    grid-column: 1 / -1;
    grid-row: 1;
    min-width: 0;
    margin-left: var(--workspace-sidebar-offset);
    z-index: 1;
  }

  .workspace-layout__content {
    grid-column: 3;
    grid-row: 3;
    min-width: 0;
    min-height: 0;
  }

  .workspace-layout__mobile-sidebar {
    display: none;
  }

  .workspace-layout--mobile {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: var(--workspace-topbar-height) minmax(0, 1fr);
  }

  .workspace-layout--mobile .workspace-layout__sidebar {
    display: none;
  }

  .workspace-layout--mobile .workspace-layout__topbar {
    grid-column: 1;
    grid-row: 1;
    margin-left: 0;
  }

  .workspace-layout--mobile .workspace-layout__content {
    grid-column: 1;
    grid-row: 2;
    /* 让内容区承担滚动，避免子页面 height:100% 在部分安卓机滚不到底 */
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
  }

  .workspace-layout--mobile .workspace-layout__mobile-sidebar {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    justify-content: flex-end;
    background: rgba(0, 0, 0, 0.5);
  }

  .workspace-layout--mobile .workspace-layout__mobile-sidebar-panel {
    height: 100%;
  }

  .mobile-sidebar-fade-enter-active,
  .mobile-sidebar-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .mobile-sidebar-fade-enter-active .workspace-layout__mobile-sidebar-panel,
  .mobile-sidebar-fade-leave-active .workspace-layout__mobile-sidebar-panel {
    transition: transform 0.2s ease;
  }

  .mobile-sidebar-fade-enter-from,
  .mobile-sidebar-fade-leave-to {
    opacity: 0;
  }

  .mobile-sidebar-fade-enter-from .workspace-layout__mobile-sidebar-panel,
  .mobile-sidebar-fade-leave-to .workspace-layout__mobile-sidebar-panel {
    transform: translateX(100%);
  }
</style>
